// @flow
// Лекало .prn: команды плоттера Graphtec (GP-GL), как их пишет драйвер
// CorelDRAW. Координаты целые, 1 единица = 0,1 мм.
//
// Разбор повторяет конвертер офлайн-каттера (convertPrnToGpgl в
// protec-cutter-offline-tauri, src/converter.js): те же команды, те же
// аргументы-координаты, тот же поворот «сдвинутых» лекал. Меняются только
// числа в командах M, D и BZ, всё остальное (шапка с полем, скоростью,
// хвост) сохраняется байт в байт. Проверка на всём корпусе лекал сервера:
// открыть и сохранить без правок — плоттер получает те же байты.

export const UNITS_PER_MM = 10;

// Порог конвертера: лекало целиком дальше 300 мм от нуля по X или Y он
// считает выгруженным на повёрнутом листе и поворачивает на 90°
export const SHIFT_THRESHOLD = 3000;

const ETX = '\x03';

// Контур замкнут, если конец там же, где начало. Допуск 0,5 мм: такой зазор
// плёнка не держит, кусок всё равно выпадает
const CLOSE_GAP = 5;
const gapOf = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
const endOf = segs => segs[segs.length - 1].pts.slice(-2);

function isArgChar(ch) {
  return (ch >= '0' && ch <= '9') || ch === '-';
}

// Какие аргументы команды — координаты: 0 = X, 1 = Y, -1 = нет
function coordAxis(op, argIndex) {
  if (op === 'M' || op === 'D') return argIndex % 2 === 1 ? 0 : 1;
  if (op === 'BZ') {
    if (argIndex === 1 || argIndex === 10) return -1;
    return argIndex % 2 === 0 ? 0 : 1;
  }
  return -1;
}

const GEOMETRY = { M: true, D: true, BZ: true };

// Участки, которые конвертер вырезает до разбора: «s,c0» в начале и «TT1s,ETX»
function hiddenRanges(text) {
  const ranges = [];
  const head = /^s,(c\d)?/.exec(text);
  if (head) ranges.push([0, head[0].length]);
  const tail = new RegExp(`TT1s,${ETX}`, 'g');
  let m;
  while ((m = tail.exec(text))) ranges.push([m.index, m.index + m[0].length]);
  return ranges;
}

export class PrnError extends Error {}

// Разбор файла. text — содержимое .prn в latin1 (как байты).
// Результат: сегменты текста (сырые куски и команды) и контуры.
export function parsePrn(text: string) {
  if (!text || (text.charCodeAt(0) === 0x50 && text.charCodeAt(1) === 0x4b)) {
    throw new PrnError('Это не файл для плоттера (похоже на документ XPS)');
  }
  const hidden = hiddenRanges(text);
  const segments = []; // { raw: string } | { cmd: number }
  const commands = []; // { op, args: [{text, value, axis}], etx, contour }
  let raw = '';
  let state = 'op';
  let op = '';
  let opText = '';
  let cmd = null;
  let arg = '';
  let h = 0;

  const flushRaw = () => {
    if (raw) {
      segments.push({ raw });
      raw = '';
    }
  };
  const finishCmd = etx => {
    cmd.etx = etx;
    commands.push(cmd);
    segments.push({ cmd: commands.length - 1 });
    cmd = null;
  };
  const pushArg = () => {
    const value = parseInt(arg, 10) || 0;
    cmd.args.push({ text: arg, value, axis: coordAxis(cmd.op, cmd.args.length + 1) });
    arg = '';
  };

  for (let i = 0; i < text.length; i += 1) {
    if (h < hidden.length && i === hidden[h][0]) {
      // Скрытый кусок: для разбора его нет, в файле остаётся как есть.
      // Он может разрезать команду («...,TT1s,ETX»): тогда он станет её частью
      const piece = text.slice(hidden[h][0], hidden[h][1]);
      if (cmd) cmd.hiddenInside = (cmd.hiddenInside || []).concat([{ at: cmd.args.length, arg, piece }]);
      else if (state === 'op' && opText) opText += piece;
      else raw += piece;
      i = hidden[h][1] - 1;
      h += 1;
      continue;
    }
    const ch = text[i];
    if (state === 'op') {
      if (isArgChar(ch)) {
        if (op === '') {
          raw += ch; // конвертер пропускает число без команды
          continue;
        }
        flushRaw();
        cmd = { op, opText, args: [], etx: false };
        op = '';
        opText = '';
        arg = ch;
        state = 'arg';
      } else if (ch === ',') {
        raw += opText + ch;
        op = '';
        opText = '';
      } else if (ch === ETX) {
        // Конвертер на ETX между буквами команды её не сбрасывает
        if (op === '') raw += ch;
        else opText += ch;
      } else {
        op += ch;
        opText += ch;
      }
    } else if (isArgChar(ch)) {
      arg += ch;
    } else if (ch === ',') {
      pushArg();
    } else if (ch === ETX) {
      if (arg !== '') throw new PrnError('Файл повреждён: конец команды посреди числа');
      finishCmd(true);
      state = 'op';
    } else {
      if (arg !== '') throw new PrnError('Файл повреждён: лишний символ посреди числа');
      finishCmd(false);
      state = 'op';
      op = ch;
      opText = ch;
    }
  }
  if (cmd) {
    // Число без запятой в самом конце конвертер не берёт: остаётся как текст
    const tail = arg;
    finishCmd(false);
    raw += tail;
  }
  raw += opText;
  flushRaw();

  // Контуры. M начинает новый. D и BZ1 продолжают текущий (у BZ1 начало
  // всегда там, где нож). BZ0 — кривая с подъёмом ножа к своему началу: если
  // начало не там, где нож, это новый контур.
  const contours = [];
  let pen = null;
  commands.forEach((c, index) => {
    if (!GEOMETRY[c.op]) return;
    const v = c.args.map(a => a.value);
    let starts = c.op === 'M' || contours.length === 0;
    if (c.op === 'BZ' && v[0] === 0 && (!pen || pen[0] !== v[1] || pen[1] !== v[2])) {
      starts = true;
      c.jump = true;
    }
    if (starts) contours.push({ commands: [] });
    c.contour = contours.length - 1;
    contours[contours.length - 1].commands.push(index);
    if (c.op === 'BZ') pen = [v[7], v[8]];
    else if (v.length >= 2) pen = [v[v.length - 2], v[v.length - 1]];
  });
  if (!contours.length) throw new PrnError('В файле нет контуров');

  const zCmd = commands.find(c => c.op === 'Z' && c.args.length >= 2);
  const sheet = zCmd ? { w: zCmd.args[0].value, h: zCmd.args[1].value } : null;

  return { text, segments, commands, contours, sheet };
}

// Координаты всех команд геометрии одним массивом пар: удобно преобразовывать
// и хранить в истории правок. index[i] — номер команды, slot — номер пары в ней.
export function coordsOf(doc) {
  const xy = [];
  const refs = [];
  doc.commands.forEach((c, ci) => {
    if (!GEOMETRY[c.op]) return;
    let x = null;
    c.args.forEach((a, ai) => {
      if (a.axis === 0) x = { ai, value: a.value };
      else if (a.axis === 1 && x) {
        refs.push({ command: ci, xArg: x.ai, yArg: ai, contour: c.contour });
        xy.push(x.value, a.value);
        x = null;
      }
    });
  });
  return { xy, refs };
}

// Поворот, который конвертер делает для «сдвинутых» лекал: (x, y) → (y − minY, maxX − x)
export function plotterTransform(xy: Array<number>) {
  let minX = Infinity; let minY = Infinity; let maxX = -Infinity;
  for (let i = 0; i < xy.length; i += 2) {
    if (xy[i] < minX) minX = xy[i];
    if (xy[i] > maxX) maxX = xy[i];
    if (xy[i + 1] < minY) minY = xy[i + 1];
  }
  const shifted = minX > SHIFT_THRESHOLD || minY > SHIFT_THRESHOLD;
  if (!shifted) return { shifted: false, xy: xy.slice() };
  const out = new Array(xy.length);
  for (let i = 0; i < xy.length; i += 2) {
    out[i] = xy[i + 1] - minY;
    out[i + 1] = maxX - xy[i];
  }
  return { shifted: true, xy: out };
}

// === Новые контуры ===
// Контур вне исходного файла (копия, вставка из буфера, нарисованный,
// скруглённый) — список команд: [{ op: 'M'|'D'|'BZ', a (у BZ), xy: [...] }].
// Пишется так же, как драйвер CorelDRAW: «M x,y,» и дальше D и BZ1, каждая
// с ETX в конце.

export function renderCmds(cmds) {
  return cmds.map(c => {
    const nums = c.xy.map(v => String(Math.round(v))).join(',');
    if (c.op === 'M') return `M${nums},`;
    if (c.op === 'D') return `D${nums},${ETX}`;
    return `BZ${c.a === 0 ? 0 : 1},${nums},${ETX}`;
  }).join('');
}

// Команды контура файла с текущими координатами
export function contourCmds(doc, contourIndex, xy, pairIndexByCommand) {
  const out = [];
  doc.contours[contourIndex].commands.forEach(ci => {
    const c = doc.commands[ci];
    const v = [];
    (pairIndexByCommand[ci] || []).forEach(p => v.push(xy[2 * p], xy[2 * p + 1]));
    if (!v.length) return;
    if (c.op === 'BZ') out.push({ op: 'BZ', a: c.args[0].value, xy: v });
    else out.push({ op: c.op, xy: v });
  });
  // Кривая-продолжение в начале: в отрыве от своего контура ей нужен переход
  if (out.length && out[0].op === 'BZ' && out[0].a !== 0) out.unshift({ op: 'M', xy: out[0].xy.slice(0, 2) });
  return out;
}

export function transformCmds(cmds, m, round = true) {
  const f = round ? Math.round : v => v;
  return cmds.map(c => {
    const xy = [];
    for (let i = 0; i + 1 < c.xy.length; i += 2) {
      xy.push(f(m[0] * c.xy[i] + m[2] * c.xy[i + 1] + m[4]), f(m[1] * c.xy[i] + m[3] * c.xy[i + 1] + m[5]));
    }
    return { ...c, xy };
  });
}

// Команда файла с (возможно, новыми) аргументами — как была записана
function renderDocCmd(c, args) {
  let out = c.opText;
  const hiddenAt = {};
  (c.hiddenInside || []).forEach(hp => { hiddenAt[hp.at] = (hiddenAt[hp.at] || '') + hp.piece; });
  args.forEach((a, ai) => {
    if (hiddenAt[ai]) out += hiddenAt[ai];
    out += a + ',';
  });
  if (hiddenAt[args.length]) out += hiddenAt[args.length];
  return out + (c.etx ? ETX : '');
}

// Файл для сохранения. xy — координаты контуров файла (по refs); deleted —
// удалённые контуры; extra — новые контуры [{ cmds }], встают после последней
// команды геометрии (в пустом файле — на место геометрии, doc.insertAt);
// replaced — контуры файла, перестроенные целиком (скругление углов, начало
// и направление реза): { номер: cmds }, пишутся на своё место в порядке реза.
export function serializePrn(doc, refs, xy, deleted = {}, extra = [], replaced = {}) {
  const values = doc.commands.map(c => c.args.map(a => a.text));
  refs.forEach((r, i) => {
    const x = Math.round(xy[2 * i]);
    const y = Math.round(xy[2 * i + 1]);
    const xArg = doc.commands[r.command].args[r.xArg];
    const yArg = doc.commands[r.command].args[r.yArg];
    if (x !== xArg.value) values[r.command][r.xArg] = String(x);
    if (y !== yArg.value) values[r.command][r.yArg] = String(y);
  });

  let lastGeometry = -1;
  doc.commands.forEach((c, i) => { if (GEOMETRY[c.op]) lastGeometry = i; });
  const extraText = extra.map(x => renderCmds(x.cmds)).join('');
  const firstCommandOf = {};
  doc.contours.forEach((ct, i) => { firstCommandOf[ct.commands[0]] = i; });

  let out = '';
  doc.segments.forEach((seg, si) => {
    if (seg.raw !== undefined) {
      out += seg.raw;
    } else {
      const c = doc.commands[seg.cmd];
      const geo = GEOMETRY[c.op];
      if (geo && deleted[c.contour]) {
        // удалён
      } else if (geo && replaced[c.contour]) {
        if (firstCommandOf[seg.cmd] === c.contour) out += renderCmds(replaced[c.contour]);
      } else {
        out += renderDocCmd(c, values[seg.cmd]);
      }
    }
    if ((lastGeometry >= 0 && seg.cmd === lastGeometry) || (lastGeometry < 0 && si === doc.insertAt)) out += extraText;
  });
  return out;
}

// === Новое лекало ===
// Шапка и хвост файла — как у остальных лекал: поле, скорость, лист. Между
// ними встаёт геометрия. Каттер режет только геометрию, но веб-резка отдаёт
// файл плоттеру целиком, поэтому шапка — от соседнего лекала той же модели.

// Самые частые на сервере (6 171 лекало из 11 206)
export const DEFAULT_HEADER = 's,c0&100,100,100,\\0,0,Z2470,1800,L0,J1,';
export const DEFAULT_TAIL = `&1,1,1,TT1s,${ETX}`;
// Угол рамки лекала на листе: у 9 051 лекала X 0, Y 0,2 мм — так ставит драйвер Корела
export const DEFAULT_CORNER = { x: 0, y: 2 };

// Шапка обычная: без поворота листа и прочих команд до геометрии
const PLAIN_HEADER = new RegExp(`^s,(c\\d)?&\\d+,\\d+,\\d+,\\\\0,0,Z\\d+,\\d+,L0,J1,(!0,1,${ETX})?$`);

// Шапка (всё до первой команды геометрии) и хвост (всё после последней)
export function templateOf(doc) {
  const values = doc.commands.map(c => c.args.map(a => a.text));
  let first = -1;
  let last = -1;
  doc.segments.forEach((seg, si) => {
    if (seg.cmd !== undefined && GEOMETRY[doc.commands[seg.cmd].op]) {
      if (first < 0) first = si;
      last = si;
    }
  });
  if (first < 0) return null;
  const text = seg => (seg.raw !== undefined ? seg.raw : renderDocCmd(doc.commands[seg.cmd], values[seg.cmd]));
  const header = doc.segments.slice(0, first).map(text).join('');
  const tail = doc.segments.slice(last + 1).map(text).join('');
  return PLAIN_HEADER.test(header) && tail === DEFAULT_TAIL ? { header, tail } : null;
}

// Файл без контуров: шапка, место под геометрию, хвост
export function emptyDoc(header = DEFAULT_HEADER, tail = DEFAULT_TAIL) {
  const z = /Z(\d+),(\d+),/.exec(header);
  return {
    text: header + tail,
    segments: [{ raw: header }, { raw: tail }],
    commands: [],
    contours: [],
    sheet: z ? { w: +z[1], h: +z[2] } : null,
    insertAt: 0,
  };
}

// === Фигуры ===
// Прямоугольник (со скруглением углов r) и эллипс по центру, размеру и углу
// поворота, в единицах плоттера. Дуги — кривые Безье, как рисует CorelDRAW.

const KAPPA = 0.5522847498307936;

function shapeOutline(shape) {
  const w = Math.max(shape.w, 1);
  const h = Math.max(shape.h, 1);
  const rad = ((shape.angle || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const P = (x, y) => [Math.round(shape.cx + x * cos - y * sin), Math.round(shape.cy + x * sin + y * cos)];
  const hw = w / 2;
  const hh = h / 2;
  if (shape.type === 'ellipse') {
    const kx = hw * KAPPA;
    const ky = hh * KAPPA;
    return [
      { op: 'M', xy: P(hw, 0) },
      { op: 'BZ', a: 1, xy: [...P(hw, 0), ...P(hw, ky), ...P(kx, hh), ...P(0, hh)] },
      { op: 'BZ', a: 1, xy: [...P(0, hh), ...P(-kx, hh), ...P(-hw, ky), ...P(-hw, 0)] },
      { op: 'BZ', a: 1, xy: [...P(-hw, 0), ...P(-hw, -ky), ...P(-kx, -hh), ...P(0, -hh)] },
      { op: 'BZ', a: 1, xy: [...P(0, -hh), ...P(kx, -hh), ...P(hw, -ky), ...P(hw, 0)] },
    ];
  }
  const r = Math.max(0, Math.min(shape.r || 0, hw, hh));
  if (r < 0.5) {
    return [
      { op: 'M', xy: P(-hw, -hh) },
      { op: 'D', xy: P(hw, -hh) },
      { op: 'D', xy: P(hw, hh) },
      { op: 'D', xy: P(-hw, hh) },
      { op: 'D', xy: P(-hw, -hh) },
    ];
  }
  const k = r * KAPPA;
  const cmds = [{ op: 'M', xy: P(-hw + r, -hh) }];
  const pen = () => cmds[cmds.length - 1].xy.slice(-2);
  const line = (x, y) => {
    const q = P(x, y);
    const at = pen();
    if (q[0] !== at[0] || q[1] !== at[1]) cmds.push({ op: 'D', xy: q });
  };
  // Дуга всегда от точки, где нож, — у BZ1 начало обязано совпадать с ним
  const arc = (x1, y1, x2, y2, x3, y3) => cmds.push({ op: 'BZ', a: 1, xy: [...pen(), ...P(x1, y1), ...P(x2, y2), ...P(x3, y3)] });
  line(hw - r, -hh);
  arc(hw - r + k, -hh, hw, -hh + r - k, hw, -hh + r);
  line(hw, hh - r);
  arc(hw, hh - r + k, hw - r + k, hh, hw - r, hh);
  line(-hw + r, hh);
  arc(-hw + r - k, hh, -hw, hh - r + k, -hw, hh - r);
  line(-hw, -hh + r);
  arc(-hw, -hh + r - k, -hw + r - k, -hh, -hw + r, -hh);
  return cmds;
}

// Фигура в командах реза: shape.reverse — резать в обратную сторону
export function shapeCmds(shape) {
  const cmds = shapeOutline(shape);
  return shape.reverse ? reverseCmds(cmds) : cmds;
}

// Фигура после преобразования. Если прямоугольник остался прямоугольником
// (сдвиг, поворот, растяжение по его осям, зеркало) — новые размер, центр и
// угол. Радиус углов при растяжении по одной оси не меняется, как в CorelDRAW,
// при пропорциональном — меняется вместе с фигурой. Перекос — null: фигура
// становится обычным контуром.
export function transformShape(shape, m) {
  const cx = m[0] * shape.cx + m[2] * shape.cy + m[4];
  const cy = m[1] * shape.cx + m[3] * shape.cy + m[5];
  const rad = ((shape.angle || 0) * Math.PI) / 180;
  const ex = [Math.cos(rad), Math.sin(rad)];
  const ey = [-Math.sin(rad), Math.cos(rad)];
  const mx = [m[0] * ex[0] + m[2] * ex[1], m[1] * ex[0] + m[3] * ex[1]];
  const my = [m[0] * ey[0] + m[2] * ey[1], m[1] * ey[0] + m[3] * ey[1]];
  const lx = Math.hypot(mx[0], mx[1]);
  const ly = Math.hypot(my[0], my[1]);
  if (!lx || !ly) return null;
  if (Math.abs((mx[0] * my[0] + mx[1] * my[1]) / (lx * ly)) > 1e-6) return null;
  const uniform = Math.abs(lx - ly) < 1e-9;
  let angle = (Math.atan2(mx[1], mx[0]) * 180) / Math.PI;
  angle = Math.round(angle * 1e6) / 1e6;
  return {
    ...shape,
    cx, cy,
    w: shape.w * lx,
    h: shape.h * ly,
    r: uniform ? (shape.r || 0) * lx : shape.r || 0,
    angle: ((angle % 360) + 360) % 360,
  };
}

// === Скругление углов ===
// Каждый острый угол между двумя прямыми отрезками контура заменяется дугой
// радиуса r (Безье). Отрезок отдаёт на скругление не больше половины своей
// длины: на коротких сторонах радиус выходит меньше. Углы у кривых и почти
// прямые (меньше 1°) не трогаются.
export function filletCmds(cmds, r) {
  if (!(r > 0)) return { cmds, count: 0 };
  const segs = [];
  let pen = null;
  let start = null;
  cmds.forEach(c => {
    if (c.op === 'M') {
      pen = c.xy.slice(0, 2);
      if (!start) start = pen.slice();
    } else if (c.op === 'D') {
      for (let i = 0; i + 1 < c.xy.length; i += 2) {
        const q = c.xy.slice(i, i + 2);
        if (pen && (q[0] !== pen[0] || q[1] !== pen[1])) segs.push({ type: 'L', p0: pen, p1: q });
        if (!pen) start = q.slice();
        pen = q;
      }
    } else if (c.op === 'BZ') {
      const p0 = c.a === 0 || !pen ? c.xy.slice(0, 2) : pen;
      if (!start) start = p0.slice();
      segs.push({ type: 'C', p0, c1: c.xy.slice(2, 4), c2: c.xy.slice(4, 6), p1: c.xy.slice(6, 8) });
      pen = c.xy.slice(6, 8);
    }
  });
  const n = segs.length;
  if (n < 2) return { cmds, count: 0 };
  const closed = pen[0] === start[0] && pen[1] === start[1];
  const unit = (a, b) => {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const l = Math.hypot(dx, dy);
    return { u: [dx / l, dy / l], l };
  };
  const fillets = new Array(n).fill(null); // скругление в конце отрезка i
  const junctions = closed ? n : n - 1;
  for (let i = 0; i < junctions; i += 1) {
    const s1 = segs[i];
    const s2 = segs[(i + 1) % n];
    if (s1.type !== 'L' || s2.type !== 'L') continue;
    const A = unit(s1.p0, s1.p1);
    const B = unit(s2.p0, s2.p1);
    const phi = Math.acos(Math.max(-1, Math.min(1, A.u[0] * B.u[0] + A.u[1] * B.u[1])));
    if (phi < Math.PI / 180 || phi > Math.PI * 179 / 180) continue;
    const t = Math.tan(phi / 2);
    const d = Math.min(r * t, A.l / 2, B.l / 2);
    const rr = d / t;
    const k = (4 / 3) * Math.tan(phi / 4) * rr;
    const P = s1.p1;
    const T1 = [P[0] - A.u[0] * d, P[1] - A.u[1] * d];
    const T2 = [P[0] + B.u[0] * d, P[1] + B.u[1] * d];
    fillets[i] = { T1, T2, C1: [T1[0] + A.u[0] * k, T1[1] + A.u[1] * k], C2: [T2[0] - B.u[0] * k, T2[1] - B.u[1] * k] };
  }
  const count = fillets.filter(Boolean).length;
  if (!count) return { cmds, count: 0 };

  const R = p => [Math.round(p[0]), Math.round(p[1])];
  const lastFillet = closed ? fillets[n - 1] : null;
  const first = R(lastFillet ? lastFillet.T2 : segs[0].p0);
  const out = [{ op: 'M', xy: first }];
  let cur = first;
  segs.forEach((sg, i) => {
    if (sg.type === 'L') {
      const end = R(fillets[i] ? fillets[i].T1 : sg.p1);
      if (end[0] !== cur[0] || end[1] !== cur[1]) out.push({ op: 'D', xy: end });
      cur = end;
    } else {
      const end = R(sg.p1);
      out.push({ op: 'BZ', a: 1, xy: [...cur, ...R(sg.c1), ...R(sg.c2), ...end] });
      cur = end;
    }
    const f = fillets[i];
    if (f) {
      const end = R(f.T2);
      out.push({ op: 'BZ', a: 1, xy: [...cur, ...R(f.C1), ...R(f.C2), ...end] });
      cur = end;
    }
  });
  return { cmds: out, count };
}

// === Начало и направление реза ===
// Нож режет контур по порядку команд: от первой точки, в ту сторону, куда
// идут отрезки. Контур — цепочка отрезков { type: 'L'|'C', p0, (c1, c2,) p1 }.

export function cmdsParts(cmds) {
  const parts = [];
  let pen = null;
  cmds.forEach(c => {
    if (c.op === 'M') {
      pen = c.xy.slice(0, 2);
    } else if (c.op === 'D') {
      for (let i = 0; i + 1 < c.xy.length; i += 2) {
        const q = c.xy.slice(i, i + 2);
        if (pen && (q[0] !== pen[0] || q[1] !== pen[1])) parts.push({ type: 'L', p0: pen, p1: q });
        pen = q;
      }
    } else if (c.op === 'BZ' && c.xy.length === 8) {
      const p0 = c.a === 0 || !pen ? c.xy.slice(0, 2) : pen;
      parts.push({ type: 'C', p0, c1: c.xy.slice(2, 4), c2: c.xy.slice(4, 6), p1: c.xy.slice(6, 8) });
      pen = c.xy.slice(6, 8);
    }
  });
  return parts;
}

export function partsCmds(parts) {
  const out = [];
  let pen = null;
  parts.forEach(p => {
    if (!pen || p.p0[0] !== pen[0] || p.p0[1] !== pen[1]) out.push({ op: 'M', xy: p.p0.slice() });
    if (p.type === 'L') out.push({ op: 'D', xy: p.p1.slice() });
    else out.push({ op: 'BZ', a: 1, xy: [...p.p0, ...p.c1, ...p.c2, ...p.p1] });
    pen = p.p1;
  });
  return out;
}

const samePoint = (a, b) => a[0] === b[0] && a[1] === b[1];

// Тот же контур, но нож идёт в обратную сторону
export function reverseCmds(cmds) {
  const parts = cmdsParts(cmds);
  if (!parts.length) return cmds;
  return partsCmds(parts.slice().reverse().map(p => (p.type === 'L'
    ? { type: 'L', p0: p.p1, p1: p.p0 }
    : { type: 'C', p0: p.p1, c1: p.c2, c2: p.c1, p1: p.p0 })));
}

// Точки, с которых может начаться рез. У замкнутого контура — все узлы
// (концы отрезков), у открытой линии — только два конца
export function cmdsVertices(cmds) {
  const parts = cmdsParts(cmds);
  if (!parts.length) return { closed: false, points: [] };
  const first = parts[0].p0;
  const last = parts[parts.length - 1].p1;
  const closed = gapOf(first, last) <= CLOSE_GAP;
  return { closed, points: closed ? parts.map(p => p.p0) : [first, last] };
}

// Рез замкнутого контура начинается с узла k (номер из cmdsVertices).
// Зазор в стыке (до 0,5 мм) закрывается прямой: иначе переход ножа посреди
// контура разрезал бы его на два
export function restartCmds(cmds, k) {
  let parts = cmdsParts(cmds);
  if (!parts.length) return cmds;
  const first = parts[0].p0;
  const last = parts[parts.length - 1].p1;
  if (gapOf(first, last) > CLOSE_GAP) return cmds;
  if (!samePoint(first, last)) parts = parts.concat([{ type: 'L', p0: last, p1: first }]);
  const at = ((k % parts.length) + parts.length) % parts.length;
  return partsCmds(parts.slice(at).concat(parts.slice(0, at)));
}

// Путь ломаной для стрелок направления: кривые — по 12 кусков
export function flattenSegments(segs) {
  const pts = [];
  let cur = null;
  segs.forEach(sg => {
    if (sg.type === 'C' && cur) {
      const [x1, y1, x2, y2, x3, y3] = sg.pts;
      for (let i = 1; i <= 12; i += 1) {
        const t = i / 12;
        const u = 1 - t;
        pts.push(
          u * u * u * cur[0] + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * x3,
          u * u * u * cur[1] + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * y3,
        );
      }
      cur = [x3, y3];
    } else {
      cur = sg.pts.slice(-2);
      pts.push(cur[0], cur[1]);
    }
  });
  return pts;
}

// Сегменты для экрана и габаритов по командам нового контура
export function cmdsSegments(cmds) {
  const segs = [];
  cmds.forEach(c => {
    if (c.op === 'M') segs.push({ type: 'M', pts: c.xy.slice(0, 2) });
    else if (c.op === 'D') {
      for (let i = 0; i + 1 < c.xy.length; i += 2) segs.push({ type: 'L', pts: c.xy.slice(i, i + 2) });
    } else if (c.op === 'BZ' && c.xy.length === 8) {
      segs.push({ type: c.a === 0 ? 'M' : 'L', pts: c.xy.slice(0, 2) });
      segs.push({ type: 'C', pts: c.xy.slice(2, 8) });
    }
  });
  if (segs.length && segs[0].type !== 'M') segs[0] = { type: 'M', pts: segs[0].pts.slice(-2) };
  return segs;
}

// === Геометрия для экрана, габаритов и превью ===

// Замкнут ли контур: конец там же, где начало (допуск CLOSE_GAP)
export function segmentsClosed(segs) {
  return segs.length > 1 && gapOf(segs[0].pts, endOf(segs)) <= CLOSE_GAP;
}

// Тот же путь от конца к началу
export function reverseSegments(segs) {
  const ends = segs.map(sg => sg.pts.slice(-2));
  const out = [{ type: 'M', pts: ends[ends.length - 1] }];
  for (let i = segs.length - 1; i >= 1; i -= 1) {
    const sg = segs[i];
    const start = ends[i - 1];
    if (sg.type === 'C') out.push({ type: 'C', pts: [...sg.pts.slice(2, 4), ...sg.pts.slice(0, 2), ...start] });
    else out.push({ type: sg.type === 'M' ? 'M' : 'L', pts: start });
  }
  return out;
}

// Что заливать на экране и в превью. Замкнутые контуры — как есть. У части
// лекал внешний контур нарезан на куски: они сцепляются по концам в одно
// кольцо. Открытая линия (надрез) не вырезает ничего — её не заливаем,
// иначе заливка замкнула бы её хордой в треугольник
export function fillPaths(list) {
  const out = [];
  const open = [];
  list.forEach(segs => {
    if (segs.length < 2) return;
    if (segmentsClosed(segs)) out.push(segs); else open.push(segs);
  });
  const used = open.map(() => false);
  open.forEach((first, i) => {
    if (used[i]) return;
    used[i] = true;
    let chain = first;
    let grew = true;
    while (grew && !segmentsClosed(chain)) {
      grew = false;
      const end = endOf(chain);
      for (let j = 0; j < open.length && !grew; j += 1) {
        if (!used[j]) {
          let next = null;
          if (gapOf(open[j][0].pts, end) <= CLOSE_GAP) next = open[j];
          else if (gapOf(endOf(open[j]), end) <= CLOSE_GAP) next = reverseSegments(open[j]);
          if (next) {
            chain = chain.concat([{ type: 'L', pts: next[0].pts.slice(-2) }], next.slice(1));
            used[j] = true;
            grew = true;
          }
        }
      }
    }
    if (segmentsClosed(chain)) out.push(chain);
  });
  return out;
}

// Экстремумы кубической кривой по одной оси
function cubicExtrema(p0, p1, p2, p3) {
  const a = -p0 + 3 * p1 - 3 * p2 + p3;
  const b = 2 * (p0 - 2 * p1 + p2);
  const c = p1 - p0;
  const ts = [];
  if (Math.abs(a) < 1e-12) {
    if (Math.abs(b) > 1e-12) ts.push(-c / b);
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const s = Math.sqrt(disc);
      ts.push((-b + s) / (2 * a), (-b - s) / (2 * a));
    }
  }
  return ts.filter(t => t > 0 && t < 1).map(t => {
    const u = 1 - t;
    return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
  });
}

// Сегменты контура: [{ type: 'M'|'L'|'C', pts: [x, y, ...] }] по текущим координатам.
// pos[k] — номер пары в xy для каждой пары команд контура (в порядке следования).
export function contourSegments(doc, contourIndex, xy, pairIndexByCommand) {
  const segs = [];
  doc.contours[contourIndex].commands.forEach(ci => {
    const c = doc.commands[ci];
    const pairs = pairIndexByCommand[ci] || [];
    const p = k => [xy[2 * pairs[k]], xy[2 * pairs[k] + 1]];
    if (c.op === 'M' && pairs.length) segs.push({ type: 'M', pts: p(0) });
    else if (c.op === 'D') pairs.forEach((_, k) => segs.push({ type: 'L', pts: p(k) }));
    else if (c.op === 'BZ' && pairs.length === 4) {
      segs.push({ type: c.jump ? 'M' : 'L', pts: p(0) });
      segs.push({ type: 'C', pts: [...p(1), ...p(2), ...p(3)] });
    }
  });
  // Контур без M в начале (первая кривая BZ0) — путь начинается переходом
  if (segs.length && segs[0].type !== 'M') segs[0] = { type: 'M', pts: segs[0].pts.slice(-2) };
  return segs;
}

export function pairIndex(refs) {
  const byCommand = {};
  refs.forEach((r, i) => { (byCommand[r.command] = byCommand[r.command] || []).push(i); });
  return byCommand;
}

// Габариты по самой линии (с учётом изгиба кривых, как в CorelDRAW)
export function segmentsBox(segs, box = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity }) {
  let cx = null;
  let cy = null;
  const add = (x, y) => {
    if (x < box.minX) box.minX = x;
    if (x > box.maxX) box.maxX = x;
    if (y < box.minY) box.minY = y;
    if (y > box.maxY) box.maxY = y;
  };
  segs.forEach(s => {
    if (s.type === 'C' && cx !== null) {
      const [x1, y1, x2, y2, x3, y3] = s.pts;
      cubicExtrema(cx, x1, x2, x3).forEach(x => add(x, cy));
      cubicExtrema(cy, y1, y2, y3).forEach(y => add(cx, y));
      add(x3, y3);
      [cx, cy] = [x3, y3];
    } else {
      const n = s.pts.length;
      add(s.pts[n - 2], s.pts[n - 1]);
      [cx, cy] = [s.pts[n - 2], s.pts[n - 1]];
    }
  });
  return box;
}

export function segmentsToSvg(segs) {
  return segs.map(s => `${s.type}${s.pts.map(v => Math.round(v * 100) / 100).join(' ')}`).join(' ');
}

// === Преобразования ===
// Матрица [a, b, c, d, e, f]: x' = a·x + c·y + e, y' = b·x + d·y + f

export function scaleAbout(sx, sy, ox, oy) {
  return [sx, 0, 0, sy, ox - sx * ox, oy - sy * oy];
}

export function rotateAbout(deg, ox, oy) {
  const r = (deg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return [cos, sin, -sin, cos, ox - cos * ox + sin * oy, oy - sin * ox - cos * oy];
}

export function translate(dx, dy) {
  return [1, 0, 0, 1, dx, dy];
}

// Новые координаты: преобразование только для пар выбранных контуров, с
// округлением до целых — размер на экране равен тому, что уйдёт в файл
export function applyTransform(xy, refs, selected, m, round = true) {
  const out = xy.slice();
  refs.forEach((r, i) => {
    if (!selected[r.contour]) return;
    const x = xy[2 * i];
    const y = xy[2 * i + 1];
    const nx = m[0] * x + m[2] * y + m[4];
    const ny = m[1] * x + m[3] * y + m[5];
    out[2 * i] = round ? Math.round(nx) : nx;
    out[2 * i + 1] = round ? Math.round(ny) : ny;
  });
  return out;
}

// Точка привязки в габаритах: ax, ay ∈ {0, 0.5, 1} (слева/центр/справа, снизу/центр/сверху)
export function anchorPoint(box, ax, ay) {
  return [box.minX + (box.maxX - box.minX) * ax, box.minY + (box.maxY - box.minY) * ay];
}

export function mm(units: number) {
  return units / UNITS_PER_MM;
}

// «70,5» или «70.5» → 70.5; пусто или мусор → null
export function parseMm(text: string) {
  const v = parseFloat(String(text).replace(',', '.').replace(/\s/g, ''));
  return Number.isFinite(v) ? v : null;
}

export function formatMm(units: number) {
  const v = Math.round(units) / UNITS_PER_MM;
  return v.toFixed(1).replace('.', ',');
}
